// src/api/axiosInstance.js
import axios from "axios";
import { setAuthToken, setRefreshToken, clearAuthTokens } from "../utils/auth";
import { API_BASE_URL } from "../config/apiConfig";
import { emptyListResponse, isEmptyListNotFound } from "./emptyResponse";

/* ─────────────────────────────────────────────────────────────
   Device Id (stable across sessions)
   - Stored locally in browser
   - Sent via X-Device-Id header (cross-domain friendly)
───────────────────────────────────────────────────────────── */
const DEVICE_KEY = "hms_device_id";

const getDeviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id =
        (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    // Worst-case fallback (still stable per tab session)
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
};

// Create a base axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Token refresh state
let lastActivityWrite = 0;
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

// OPTIONAL: notify the app to logout without forcing a page reload
const notifyLogout = () => {
  try {
    window.dispatchEvent(new CustomEvent("auth:logout"));
  } catch {
    // ignore
  }
};

// Request interceptor: attach access token + device id
axiosInstance.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};

    // ✅ always send device id header (server reads this first)
    config.headers["X-Device-Id"] = getDeviceId();

    // any API call made by the app counts as session activity (shared across tabs)
    try {
      const now = Date.now();
      if (now - lastActivityWrite > 5000) {
        lastActivityWrite = now;
        localStorage.setItem("hms_last_activity", String(now));
      }
    } catch {
      // ignore
    }

    const token = localStorage.getItem("jwtToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Helper: do not refresh for auth endpoints or explicitly skipped requests
const shouldSkipAuthHandling = (config) => {
  if (!config) return true;
  if (config.skipAuthHandling) return true;

  const url = String(config.url || "").toLowerCase();

  return (
    url.includes("/auth/login") ||
    url.includes("/auth/refresh-token") ||
    url.includes("/auth/resend-confirmation")
  );
};

// Response interceptor: handle refresh on 401 for protected endpoints only
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    if (!error?.response) return Promise.reject(error);

    // "No rooms found" etc. is an empty list, not an error (see emptyResponse.js)
    if (isEmptyListNotFound(error)) return emptyListResponse(error);

    const status = error.response.status;

    if (shouldSkipAuthHandling(originalRequest)) {
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            // ✅ keep device id header as well
            originalRequest.headers["X-Device-Id"] = getDeviceId();
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("jwtRefreshToken");
      if (!refreshToken) {
        clearAuthTokens();
        notifyLogout();
        processQueue(error, null);
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        // Dedicated client (no interceptors)
        const refreshClient = axios.create({
          baseURL: API_BASE_URL,
          headers: {
            "Content-Type": "application/json",
            // ✅ send device id for refresh as well (harmless, sometimes useful)
            "X-Device-Id": getDeviceId(),
          },
          withCredentials: true,
          timeout: 60000,
        });

        const refreshResp = await refreshClient.post(
          "/Auth/refresh-token",
          { refreshToken },
          { validateStatus: (s) => s < 500 }
        );

        if (refreshResp.status !== 200 || !refreshResp.data?.accessToken) {
          throw new Error("Refresh token rejected");
        }

        const newAccessToken = refreshResp.data.accessToken;

        setAuthToken(newAccessToken);

        if (refreshResp.data?.refreshToken) {
          setRefreshToken(refreshResp.data.refreshToken);
        }

        axiosInstance.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        originalRequest.headers["X-Device-Id"] = getDeviceId();

        return axiosInstance(originalRequest);
      } catch (err) {
        processQueue(err, null);
        // Only end the session when the server actually rejected the refresh token.
        // Network errors / 5xx must not log out someone who is actively working.
        if (err?.message === "Refresh token rejected") {
          clearAuthTokens();
          notifyLogout();
        }
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
